import axios from 'axios';

// Base URL for the API
// const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/api';
const API_ASSET_BASE_URL = (process.env.REACT_APP_API_ASSET_BASE_URL ?? API_BASE_URL.replace(/\/api\/?$/, '')).replace(/\/+$/, '');

// Base URL for the new projects API
const PROJECTS_API_BASE_URL = 'https://rclearance.costech.or.tz/api/v1/nfast';

// Base URL for RIMS statistics API
const RIMS_API_BASE_URL = 'https://rclearance.costech.or.tz/api/v1/rims/statistics';

const BACKEND_ASSET_FIELDS = [
  'image',
  'image_url',
  'background_image',
  'document',
  'file',
  'attachment',
  'icon',
  'icon_url',
  'logo'
];

export const resolveBackendAssetUrl = (value, fallback = '') => {
  if (!value || typeof value !== 'string') {
    return fallback;
  }

  const trimmedValue = value.trim();
  if (!trimmedValue || trimmedValue === '#') {
    return fallback;
  }

  if (/^(mailto:|tel:|#)/i.test(trimmedValue)) {
    return trimmedValue;
  }

  try {
    const url = new URL(trimmedValue);
    if (url.hostname === 'localhost' || url.hostname === '127.0.0.1') {
      return `${API_ASSET_BASE_URL}${url.pathname}${url.search}${url.hash}`;
    }
    return trimmedValue;
  } catch (error) {
    // Continue below for relative backend paths.
  }

  if (trimmedValue.startsWith('/uploads/')) {
    return `${API_ASSET_BASE_URL}${trimmedValue}`;
  }

  if (trimmedValue.startsWith('uploads/')) {
    return `${API_ASSET_BASE_URL}/${trimmedValue}`;
  }

  if (trimmedValue.startsWith('/')) {
    return trimmedValue;
  }

  return `${API_ASSET_BASE_URL}/${trimmedValue.replace(/^\/+/, '')}`;
};

const normalizeBackendItemAssets = (item) => {
  if (!item || typeof item !== 'object' || Array.isArray(item)) {
    return item;
  }

  return BACKEND_ASSET_FIELDS.reduce((normalizedItem, field) => {
    if (typeof normalizedItem[field] === 'string') {
      normalizedItem[field] = resolveBackendAssetUrl(normalizedItem[field], normalizedItem[field]);
    }
    return normalizedItem;
  }, { ...item });
};

const normalizeBackendItemList = (items) => {
  if (!Array.isArray(items)) {
    return [];
  }

  return items.map((item) => normalizeBackendItemAssets(item));
};

const normalizeVisitorCounts = (payload) => {
  const data = payload?.returnData || payload?.data || payload || {};

  return {
    month: Number(data.month || data.this_month || data.month_count || 0),
    today: Number(data.today || data.today_count || data.day || 0),
  };
};

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Token storage
let authToken = null;

/**
 * Login and get access token
 * @param {string} email - User email
 * @param {string} password - User password
 * @returns {Promise<string>} - Access token
 */
export const login = async (email = 'admin@admin.com', password = 'admin') => {
  try {
    const response = await apiClient.post('/auth/login', {
      email,
      password,
    });


    // Check multiple possible locations for the access token
    let token = null;

    if (response.data) {
      // Check direct access_token
      if (response.data.access_token) {
        token = response.data.access_token;
      }
      // Check in returnData
      else if (response.data.returnData && response.data.returnData.access_token) {
        token = response.data.returnData.access_token;
      }
      // Check if returnData itself is the token (string)
      else if (response.data.returnData && typeof response.data.returnData === 'string') {
        token = response.data.returnData;
      }
      // Check if returnData is an object with token property
      else if (response.data.returnData && response.data.returnData.token) {
        token = response.data.returnData.token;
      }
    }

    if (token) {
      authToken = token;
      return authToken;
    }

    // Log the full response structure for debugging
    console.error('Token not found in response. Full response structure:', JSON.stringify(response.data, null, 2));
    throw new Error('No access token received');
  } catch (error) {
    console.error('Login error:', error);
    if (error.response) {
      console.error('Login response status:', error.response.status);
      console.error('Login response data:', error.response.data);
    } else if (error.request) {
      console.error('No login response received:', error.request);
    } else {
      console.error('Login error setting up request:', error.message);
    }
    throw error;
  }
};

/**
 * Get access token (login if not already authenticated)
 * @returns {Promise<string>} - Access token
 */
const getAuthToken = async () => {
  if (authToken) {
    return authToken;
  }

  return await login();
};

/**
 * Fetch news list from API
 * @returns {Promise<Array>} - Array of news items
 */
export const getNewsList = async () => {
  try {

    // Get authentication token
    const token = await getAuthToken();

    // Make authenticated request
    const response = await apiClient.get('/news/ilist', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });


    if (response.data && response.data.status === 'OK' && response.data.returnData) {
      const newsItems = response.data.returnData.list_of_item || [];
      return normalizeBackendItemList(newsItems);
    }

    console.warn('API returned empty or invalid response');
    return [];
  } catch (error) {
    console.error('Error fetching news:', error);
    if (error.response) {
      console.error('Response status:', error.response.status);
      console.error('Response data:', error.response.data);
    } else if (error.request) {
      console.error('No response received:', error.request);
    } else {
      console.error('Error setting up request:', error.message);
    }
    throw error;
  }
};

/**
 * Track one website visit and return current visitor counts.
 * @param {string} visitorId - Browser-generated visitor identifier.
 * @returns {Promise<{month: number, today: number}>}
 */
export const trackWebsiteVisit = async (visitorId) => {
  try {
    const response = await apiClient.post('/websiteVisitor/track', {
      visitor_id: visitorId,
    });

    if (response.data && response.data.status === 'OK') {
      return normalizeVisitorCounts(response.data);
    }

    throw new Error(response.data?.errorMessage || 'Visitor counter returned an invalid response');
  } catch (error) {
    console.error('Error tracking website visit:', error);
    throw error;
  }
};

/**
 * Fetch website visitor counts without incrementing.
 * @returns {Promise<{month: number, today: number}>}
 */
export const getWebsiteVisitorCounts = async () => {
  try {
    const response = await apiClient.get('/websiteVisitor/counts');

    if (response.data && response.data.status === 'OK') {
      return normalizeVisitorCounts(response.data);
    }

    throw new Error(response.data?.errorMessage || 'Visitor counter returned an invalid response');
  } catch (error) {
    console.error('Error fetching website visitor counts:', error);
    throw error;
  }
};

/**
 * Fetch single news item by ID from API
 * @param {number|string} id - News item ID
 * @returns {Promise<Object>} - News item with full details
 */
export const getNewsById = async (id) => {
  try {

    // Get authentication token
    const token = await getAuthToken();

    // Make authenticated request - try different possible endpoints
    let response;
    try {
      // Try endpoint with ID
      response = await apiClient.get(`/news/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
    } catch (err) {
      // If that fails, try alternative endpoint
      try {
        response = await apiClient.get(`/news/detail/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
      } catch (err2) {
        // If both fail, try with query parameter
        response = await apiClient.get(`/news/ilist?id=${id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
      }
    }


    if (response.data && response.data.status === 'OK') {
      const newsItem = response.data.returnData || response.data.data || response.data;
      return normalizeBackendItemAssets(newsItem);
    }

    console.warn('API returned empty or invalid response');
    return null;
  } catch (error) {
    console.error('Error fetching news item:', error);
    if (error.response) {
      console.error('Response status:', error.response.status);
      console.error('Response data:', error.response.data);
    } else if (error.request) {
      console.error('No response received:', error.request);
    } else {
      console.error('Error setting up request:', error.message);
    }
    throw error;
  }
};

/**
 * Generate slug from title
 * @param {string} title - News title
 * @returns {string} - URL-friendly slug
 */
export const generateSlug = (title) => {
  return title
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '') // Remove special characters
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-'); // Replace multiple hyphens with single hyphen
};

/**
 * Format date from API response or use current date
 * @param {string|null} dateString - Date string from API
 * @returns {string} - Formatted date
 */
export const formatDate = (dateString) => {
  if (!dateString) {
    // Use current date if no date provided
    const date = new Date();
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  }

  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  } catch (error) {
    return new Date().toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  }
};

/**
 * Fetch financial reports list from API
 * @returns {Promise<Array>} - Array of financial report items
 */
export const getFinancialReports = async () => {
  try {

    // Get authentication token
    const token = await getAuthToken();

    // Make authenticated request
    const response = await apiClient.get('/financialReport/ilist', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });


    if (response.data && response.data.status === 'OK' && response.data.returnData) {
      const reports = response.data.returnData.list_of_item || [];
      return normalizeBackendItemList(reports);
    }

    console.warn('API returned empty or invalid response');
    return [];
  } catch (error) {
    console.error('Error fetching financial reports:', error);
    if (error.response) {
      console.error('Response status:', error.response.status);
      console.error('Response data:', error.response.data);
    } else if (error.request) {
      console.error('No response received:', error.request);
    } else {
      console.error('Error setting up request:', error.message);
    }
    throw error;
  }
};

/**
 * Fetch sponsorships list from API
 * @returns {Promise<Array>} - Array of sponsorship items
 */
export const getSponsorships = async () => {
  try {

    // Get authentication token
    const token = await getAuthToken();

    // Make authenticated request
    const response = await apiClient.get('/sponsorships/ilist', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });


    if (response.data && response.data.status === 'OK' && response.data.returnData) {
      const sponsorships = response.data.returnData.list_of_item || [];
      return normalizeBackendItemList(sponsorships);
    }

    console.warn('API returned empty or invalid response');
    return [];
  } catch (error) {
    console.error('Error fetching sponsorships:', error);
    if (error.response) {
      console.error('Response status:', error.response.status);
      console.error('Response data:', error.response.data);
    } else if (error.request) {
      console.error('No response received:', error.request);
    } else {
      console.error('Error setting up request:', error.message);
    }
    throw error;
  }
};

/**
 * Fetch magazines list from API
 * @returns {Promise<Array>} - Array of magazine items
 */
export const getMagazines = async () => {
  try {

    // Get authentication token
    const token = await getAuthToken();

    // Make authenticated request
    const response = await apiClient.get('/magazine/ilist', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });


    if (response.data && response.data.status === 'OK' && response.data.returnData) {
      const magazines = response.data.returnData.list_of_item || [];
      return normalizeBackendItemList(magazines);
    }

    console.warn('API returned empty or invalid response');
    return [];
  } catch (error) {
    console.error('Error fetching magazines:', error);
    if (error.response) {
      console.error('Response status:', error.response.status);
      console.error('Response data:', error.response.data);
    } else if (error.request) {
      console.error('No response received:', error.request);
    } else {
      console.error('Error setting up request:', error.message);
    }
    throw error;
  }
};

/**
 * Fetch journals list from API
 * @returns {Promise<Array>} - Array of journal items
 */
export const getJournals = async () => {
  try {
    // Get authentication token
    const token = await getAuthToken();

    // Make authenticated request
    const response = await apiClient.get('/journal/ilist', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (response.data && response.data.status === 'OK' && response.data.returnData && response.data.returnData.list_of_item) {
      return normalizeBackendItemList(response.data.returnData.list_of_item);
    }

    console.warn('API returned empty or invalid response for journals');
    return [];
  } catch (error) {
    console.error('Error fetching journals:', error);
    if (error.response) {
      console.error('Response status:', error.response.status);
      console.error('Response data:', error.response.data);
    } else if (error.request) {
      console.error('No response received:', error.request);
    } else {
      console.error('Error setting up request:', error.message);
    }
    throw error;
  }
};

/**
 * Fetch books list from API
 * @returns {Promise<Array>} - Array of book items
 */
export const getBooks = async () => {
  try {

    // Get authentication token
    const token = await getAuthToken();

    // Make authenticated request
    const response = await apiClient.get('/books/ilist', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });


    if (response.data && response.data.status === 'OK' && response.data.returnData) {
      const books = response.data.returnData.list_of_item || [];
      return normalizeBackendItemList(books);
    }

    console.warn('API returned empty or invalid response');
    return [];
  } catch (error) {
    console.error('Error fetching books:', error);
    if (error.response) {
      console.error('Response status:', error.response.status);
      console.error('Response data:', error.response.data);
    } else if (error.request) {
      console.error('No response received:', error.request);
    } else {
      console.error('Error setting up request:', error.message);
    }
    throw error;
  }
};

/**
 * Fetch newsletters list from API
 * @returns {Promise<Array>} - Array of newsletter items
 */
export const getNewsletters = async () => {
  try {

    // Get authentication token
    const token = await getAuthToken();

    // Make authenticated request
    const response = await apiClient.get('/newsLetter/ilist', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });


    if (response.data && response.data.status === 'OK' && response.data.returnData) {
      const newsletters = response.data.returnData.list_of_item || [];
      return normalizeBackendItemList(newsletters);
    }

    console.warn('API returned empty or invalid response');
    return [];
  } catch (error) {
    console.error('Error fetching newsletters:', error);
    if (error.response) {
      console.error('Response status:', error.response.status);
      console.error('Response data:', error.response.data);
    } else if (error.request) {
      console.error('No response received:', error.request);
    } else {
      console.error('Error setting up request:', error.message);
    }
    throw error;
  }
};

/**
 * Fetch statements list from API
 * @returns {Promise<Array>} - Array of statement items
 */
export const getStatements = async () => {
  try {

    // Get authentication token
    const token = await getAuthToken();

    // Make authenticated request
    const response = await apiClient.get('/statement/ilist', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });


    if (response.data && response.data.status === 'OK' && response.data.returnData) {
      const statements = response.data.returnData.list_of_item || [];
      return normalizeBackendItemList(statements);
    }

    console.warn('API returned empty or invalid response');
    return [];
  } catch (error) {
    console.error('Error fetching statements:', error);
    if (error.response) {
      console.error('Response status:', error.response.status);
      console.error('Response data:', error.response.data);
    } else if (error.request) {
      console.error('No response received:', error.request);
    } else {
      console.error('Error setting up request:', error.message);
    }
    throw error;
  }
};

/**
 * Fetch press releases list from API
 * @returns {Promise<Array>} - Array of press release items
 */
export const getPressReleases = async () => {
  try {

    // Get authentication token
    const token = await getAuthToken();

    // Make authenticated request
    const response = await apiClient.get('/pressRelease/ilist', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });


    if (response.data && response.data.status === 'OK' && response.data.returnData) {
      const pressReleases = response.data.returnData.list_of_item || [];
      return normalizeBackendItemList(pressReleases);
    }

    console.warn('API returned empty or invalid response');
    return [];
  } catch (error) {
    console.error('Error fetching press releases:', error);
    if (error.response) {
      console.error('Response status:', error.response.status);
      console.error('Response data:', error.response.data);
    } else if (error.request) {
      console.error('No response received:', error.request);
    } else {
      console.error('Error setting up request:', error.message);
    }
    throw error;
  }
};

/**
 * Fetch COSTECH videos list from API
 * @returns {Promise<Array>} - Array of video items
 */
export const getCostechVideos = async () => {
  try {

    // Get authentication token
    const token = await getAuthToken();

    // Make authenticated request
    const response = await apiClient.get('/costechVideo/ilist', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });


    if (response.data && response.data.status === 'OK' && response.data.returnData) {
      const videos = response.data.returnData.list_of_item || [];
      return normalizeBackendItemList(videos);
    }

    console.warn('API returned empty or invalid response');
    return [];
  } catch (error) {
    console.error('Error fetching COSTECH videos:', error);
    if (error.response) {
      console.error('Response status:', error.response.status);
      console.error('Response data:', error.response.data);
    } else if (error.request) {
      console.error('No response received:', error.request);
    } else {
      console.error('Error setting up request:', error.message);
    }
    throw error;
  }
};

/**
 * Fetch reports list from API
 * @returns {Promise<Array>} - Array of report items
 */
export const getReports = async () => {
  try {

    // Get authentication token
    const token = await getAuthToken();

    // Make authenticated request
    const response = await apiClient.get('/reports/ilist', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });


    if (response.data && response.data.status === 'OK' && response.data.returnData) {
      const reports = response.data.returnData.list_of_item || [];
      return normalizeBackendItemList(reports);
    }

    console.warn('API returned empty or invalid response');
    return [];
  } catch (error) {
    console.error('Error fetching reports:', error);
    if (error.response) {
      console.error('Response status:', error.response.status);
      console.error('Response data:', error.response.data);
    } else if (error.request) {
      console.error('No response received:', error.request);
    } else {
      console.error('Error setting up request:', error.message);
    }
    throw error;
  }
};

/**
 * Fetch acts and legal documents list from API
 * @returns {Promise<Array>} - Array of acts and legal items
 */
export const getActsAndLegal = async () => {
  try {

    // Get authentication token
    const token = await getAuthToken();

    // Make authenticated request
    const response = await apiClient.get('/actsandlegal/ilist', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });


    if (response.data && response.data.status === 'OK' && response.data.returnData) {
      const actsAndLegal = response.data.returnData.list_of_item || [];
      return normalizeBackendItemList(actsAndLegal);
    }

    console.warn('API returned empty or invalid response');
    return [];
  } catch (error) {
    console.error('Error fetching acts and legal documents:', error);
    if (error.response) {
      console.error('Response status:', error.response.status);
      console.error('Response data:', error.response.data);
    } else if (error.request) {
      console.error('No response received:', error.request);
    } else {
      console.error('Error setting up request:', error.message);
    }
    throw error;
  }
};

/**
 * Fetch policies list from API
 * @returns {Promise<Array>} - Array of policy items
 */
export const getPolicies = async () => {
  try {

    // Get authentication token
    const token = await getAuthToken();

    // Make authenticated request
    const response = await apiClient.get('/policies/ilist', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });


    if (response.data && response.data.status === 'OK' && response.data.returnData) {
      const policies = response.data.returnData.list_of_item || [];
      return normalizeBackendItemList(policies);
    }

    console.warn('API returned empty or invalid response');
    return [];
  } catch (error) {
    console.error('Error fetching policies:', error);
    if (error.response) {
      console.error('Response status:', error.response.status);
      console.error('Response data:', error.response.data);
    } else if (error.request) {
      console.error('No response received:', error.request);
    } else {
      console.error('Error setting up request:', error.message);
    }
    throw error;
  }
};

/**
 * Fetch strategic plans list from API
 * @returns {Promise<Array>} - Array of strategic plan items
 */
export const getStrategicPlans = async () => {
  try {

    // Get authentication token
    const token = await getAuthToken();

    // Make authenticated request
    const response = await apiClient.get('/strategicPlan/ilist', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });


    if (response.data && response.data.status === 'OK' && response.data.returnData) {
      const strategicPlans = response.data.returnData.list_of_item || [];
      return normalizeBackendItemList(strategicPlans);
    }

    console.warn('API returned empty or invalid response');
    return [];
  } catch (error) {
    console.error('Error fetching strategic plans:', error);
    if (error.response) {
      console.error('Response status:', error.response.status);
      console.error('Response data:', error.response.data);
    } else if (error.request) {
      console.error('No response received:', error.request);
    } else {
      console.error('Error setting up request:', error.message);
    }
    throw error;
  }
};

/**
 * Fetch guideline documents list from API
 * @returns {Promise<Array>} - Array of guideline document items
 */
export const getGuidelineDocuments = async () => {
  try {

    // Get authentication token
    const token = await getAuthToken();

    // Make authenticated request
    const response = await apiClient.get('/guidelineDocuments/ilist', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });


    if (response.data && response.data.status === 'OK' && response.data.returnData) {
      const guidelineDocuments = response.data.returnData.list_of_item || [];
      return normalizeBackendItemList(guidelineDocuments);
    }

    console.warn('API returned empty or invalid response');
    return [];
  } catch (error) {
    console.error('Error fetching guideline documents:', error);
    if (error.response) {
      console.error('Response status:', error.response.status);
      console.error('Response data:', error.response.data);
    } else if (error.request) {
      console.error('No response received:', error.request);
    } else {
      console.error('Error setting up request:', error.message);
    }
    throw error;
  }
};

/**
 * Fetch conferences list from API
 * @returns {Promise<Array>} - Array of conference items
 */
export const getConferences = async () => {
  try {

    // Get authentication token
    const token = await getAuthToken();

    // Make authenticated request
    const response = await apiClient.get('/conference/ilist', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });


    if (response.data && response.data.status === 'OK' && response.data.returnData) {
      const conferences = response.data.returnData.list_of_item || [];
      return normalizeBackendItemList(conferences);
    }

    console.warn('API returned empty or invalid response');
    return [];
  } catch (error) {
    console.error('Error fetching conferences:', error);
    if (error.response) {
      console.error('Response status:', error.response.status);
      console.error('Response data:', error.response.data);
    } else if (error.request) {
      console.error('No response received:', error.request);
    } else {
      console.error('Error setting up request:', error.message);
    }
    throw error;
  }
};

/**
 * Fetch exhibitions list from API
 * @returns {Promise<Array>} - Array of exhibition items
 */
export const getExhibitions = async () => {
  try {

    // Get authentication token
    const token = await getAuthToken();

    // Make authenticated request
    const response = await apiClient.get('/exhibition/ilist', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });


    if (response.data && response.data.status === 'OK' && response.data.returnData) {
      const exhibitions = response.data.returnData.list_of_item || [];
      return normalizeBackendItemList(exhibitions);
    }

    console.warn('API returned empty or invalid response');
    return [];
  } catch (error) {
    console.error('Error fetching exhibitions:', error);
    if (error.response) {
      console.error('Response status:', error.response.status);
      console.error('Response data:', error.response.data);
    } else if (error.request) {
      console.error('No response received:', error.request);
    } else {
      console.error('Error setting up request:', error.message);
    }
    throw error;
  }
};

/**
 * Fetch social media platforms list from API
 * @returns {Promise<Array>} - Array of social media platform items
 */
export const getSocialMediaPlatforms = async () => {
  try {
    // Get authentication token
    const token = await getAuthToken();

    // Make authenticated request
    const response = await apiClient.get('/socialMediaPlatform/ilist', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (response.data && response.data.status === 'OK' && response.data.returnData && response.data.returnData.list_of_item) {
      return normalizeBackendItemList(response.data.returnData.list_of_item);
    }

    console.warn('API returned empty or invalid response for social media platforms');
    return [];
  } catch (error) {
    console.error('Error fetching social media platforms:', error);
    if (error.response) {
      console.error('Response status:', error.response.status);
      console.error('Response data:', error.response.data);
    } else if (error.request) {
      console.error('No response received:', error.request);
    } else {
      console.error('Error setting up request:', error.message);
    }
    throw error;
  }
};

/**
 * Fetch footer e-resources list from API
 * @returns {Promise<Array>} - Array of e-resource items
 */
export const getFooterEresources = async () => {
  try {
    // Get authentication token
    const token = await getAuthToken();

    // Make authenticated request
    const response = await apiClient.get('/footerEresource/ilist', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (response.data && response.data.status === 'OK' && response.data.returnData && response.data.returnData.list_of_item) {
      return normalizeBackendItemList(response.data.returnData.list_of_item);
    }

    console.warn('API returned empty or invalid response for footer e-resources');
    return [];
  } catch (error) {
    console.error('Error fetching footer e-resources:', error);
    if (error.response) {
      console.error('Response status:', error.response.status);
      console.error('Response data:', error.response.data);
    } else if (error.request) {
      console.error('No response received:', error.request);
    } else {
      console.error('Error setting up request:', error.message);
    }
    throw error;
  }
};

/**
 * Fetch footer contact us information from API
 * @returns {Promise<Array>} - Array of contact items
 */
export const getFooterContactUs = async () => {
  try {
    // Get authentication token
    const token = await getAuthToken();

    // Make authenticated request
    const response = await apiClient.get('/footerContactUs/ilist', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (response.data && response.data.status === 'OK' && response.data.returnData && response.data.returnData.list_of_item) {
      return normalizeBackendItemList(response.data.returnData.list_of_item);
    }

    console.warn('API returned empty or invalid response for footer contact us');
    return [];
  } catch (error) {
    console.error('Error fetching footer contact us:', error);
    if (error.response) {
      console.error('Response status:', error.response.status);
      console.error('Response data:', error.response.data);
    } else if (error.request) {
      console.error('No response received:', error.request);
    } else {
      console.error('Error setting up request:', error.message);
    }
    throw error;
  }
};

/**
 * Fetch footer quick links list from API
 * @returns {Promise<Array>} - Array of quick link items
 */
export const getFooterQuickLinks = async () => {
  try {
    // Get authentication token
    const token = await getAuthToken();

    // Make authenticated request
    const response = await apiClient.get('/footerQuickLink/ilist', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (response.data && response.data.status === 'OK' && response.data.returnData && response.data.returnData.list_of_item) {
      return normalizeBackendItemList(response.data.returnData.list_of_item);
    }

    console.warn('API returned empty or invalid response for footer quick links');
    return [];
  } catch (error) {
    console.error('Error fetching footer quick links:', error);
    if (error.response) {
      console.error('Response status:', error.response.status);
      console.error('Response data:', error.response.data);
    } else if (error.request) {
      console.error('No response received:', error.request);
    } else {
      console.error('Error setting up request:', error.message);
    }
    throw error;
  }
};

/**
 * Fetch ongoing projects list from API
 * @returns {Promise<Array>} - Array of ongoing project items
 */
export const getOngoingProjects = async () => {
  try {

    // Get authentication token
    const token = await getAuthToken();

    // Make authenticated request
    const response = await apiClient.get('/ongoingProject/ilist', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });


    if (response.data && response.data.status === 'OK' && response.data.returnData) {
      const projects = response.data.returnData.list_of_item || [];
      return normalizeBackendItemList(projects);
    }

    console.warn('API returned empty or invalid response');
    return [];
  } catch (error) {
    console.error('Error fetching ongoing projects:', error);
    if (error.response) {
      console.error('Response status:', error.response.status);
      console.error('Response data:', error.response.data);
    } else if (error.request) {
      console.error('No response received:', error.request);
    } else {
      console.error('Error setting up request:', error.message);
    }
    throw error;
  }
};

/**
 * Fetch partnership areas list from API
 * @returns {Promise<Array>} - Array of partnership area items
 */
export const getPartnershipAreas = async () => {
  try {

    // Get authentication token
    const token = await getAuthToken();

    // Make authenticated request
    const response = await apiClient.get('/areaOfPartnership/ilist', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });


    if (response.data && response.data.status === 'OK' && response.data.returnData) {
      const areas = response.data.returnData.list_of_item || [];
      return normalizeBackendItemList(areas);
    }

    console.warn('API returned empty or invalid response');
    return [];
  } catch (error) {
    console.error('Error fetching partnership areas:', error);
    if (error.response) {
      console.error('Response status:', error.response.status);
      console.error('Response data:', error.response.data);
    } else if (error.request) {
      console.error('No response received:', error.request);
    } else {
      console.error('Error setting up request:', error.message);
    }
    throw error;
  }
};

/**
 * Fetch fellowship grants list from API
 * @returns {Promise<Array>} - Array of fellowship grant items
 */
export const getFellowshipGrants = async () => {
  try {

    // Get authentication token
    const token = await getAuthToken();

    // Make authenticated request
    const response = await apiClient.get('/fellowshipGrants/ilist', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });


    if (response.data && response.data.status === 'OK' && response.data.returnData) {
      const grants = response.data.returnData.list_of_item || [];
      return normalizeBackendItemList(grants);
    }

    console.warn('API returned empty or invalid response');
    return [];
  } catch (error) {
    console.error('Error fetching fellowship grants:', error);
    if (error.response) {
      console.error('Response status:', error.response.status);
      console.error('Response data:', error.response.data);
    } else if (error.request) {
      console.error('No response received:', error.request);
    } else {
      console.error('Error setting up request:', error.message);
    }
    throw error;
  }
};

/**
 * Fetch online services list from API
 * @returns {Promise<Array>} - Array of online service items
 */
export const getOnlineServices = async () => {
  try {

    // Get authentication token
    const token = await getAuthToken();

    // Make authenticated request
    const response = await apiClient.get('/onlineService/ilist', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });


    if (response.data && response.data.status === 'OK' && response.data.returnData) {
      const services = response.data.returnData.list_of_item || [];
      return normalizeBackendItemList(services);
    }

    console.warn('API returned empty or invalid response');
    return [];
  } catch (error) {
    console.error('Error fetching online services:', error);
    if (error.response) {
      console.error('Response status:', error.response.status);
      console.error('Response data:', error.response.data);
    } else if (error.request) {
      console.error('No response received:', error.request);
    } else {
      console.error('Error setting up request:', error.message);
    }
    throw error;
  }
};

/**
 * Fetch hero items list from API
 * @returns {Promise<Array>} - Array of hero items
 */
export const getHero = async () => {
  try {

    // Get authentication token
    const token = await getAuthToken();

    // Make authenticated request
    const response = await apiClient.get('/hero/ilist', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });


    if (response.data && response.data.status === 'OK' && response.data.returnData) {
      const heroItems = response.data.returnData.list_of_item || [];
      return normalizeBackendItemList(heroItems);
    }

    console.warn('API returned empty or invalid response');
    return [];
  } catch (error) {
    console.error('Error fetching hero items:', error);
    if (error.response) {
      console.error('Response status:', error.response.status);
      console.error('Response data:', error.response.data);
    } else if (error.request) {
      console.error('No response received:', error.request);
    } else {
      console.error('Error setting up request:', error.message);
    }
    throw error;
  }
};

/** Max slides shown on the homepage hero carousel */
export const HERO_CAROUSEL_LIMIT = 5;

/**
 * Sort hero items newest first (by common API date fields, then by id).
 * @param {Array} items - Raw hero list from API
 * @returns {Array}
 */
export const sortHeroItemsLatestFirst = (items) => {
  if (!Array.isArray(items) || items.length === 0) {
    return [];
  }

  const timestamp = (item) => {
    const raw =
      item.created_at ||
      item.updated_at ||
      item.date ||
      item.createdAt ||
      item.uploaded_at ||
      item.timestamp;
    if (raw == null || raw === '') {
      return 0;
    }
    const t = new Date(raw).getTime();
    return Number.isFinite(t) ? t : 0;
  };

  const numericId = (item) => {
    const id = item.id ?? item.hero_id;
    const n = Number(id);
    return Number.isFinite(n) ? n : 0;
  };

  return [...items].sort((a, b) => {
    const diff = timestamp(b) - timestamp(a);
    if (diff !== 0) {
      return diff;
    }
    return numericId(b) - numericId(a);
  });
};

/**
 * Fetch COSTECH funded projects list from API with filters
 * @param {Object} filters - Filter parameters (page_no, page_size, program_id, gender_id, status_id, funder_id)
 * @returns {Promise<Object>} - Object with projects array and pagination info
 */
export const getCostechFundedProjects = async (filters = {}) => {
  try {

    const payload = {
      page_no: filters.page_no || 1,
      page_size: filters.page_size || 10,
      ...(filters.program_id && { program_id: filters.program_id }),
      ...(filters.gender_id && { gender_id: filters.gender_id }),
      ...(filters.status_id && { status_id: filters.status_id }),
      ...(filters.funder_id && { funder_id: filters.funder_id }),
    };

    const response = await axios.post(`${PROJECTS_API_BASE_URL}/project/list`, payload, {
      timeout: 15000,
      headers: {
        'Content-Type': 'application/json',
      },
    });


    // Handle paginated response structure
    if (response.data && response.data.data && Array.isArray(response.data.data)) {
      const projects = response.data.data;
      const meta = response.data.meta || {};
      return {
        projects,
        pagination: {
          current_page: meta.page || filters.page_no || 1,
          total: meta.total || projects.length,
          page_size: meta.page_size || filters.page_size || 10,
          total_pages: meta.pages || Math.ceil((meta.total || projects.length) / (meta.page_size || filters.page_size || 10)),
        },
      };
    }

    // Fallback: handle direct array response
    if (response.data && Array.isArray(response.data)) {
      return {
        projects: response.data,
        pagination: {
          current_page: 1,
          total: response.data.length,
          page_size: filters.page_size || 10,
          total_pages: 1,
        },
      };
    }

    console.warn('API returned empty or invalid response');
    return { projects: [], pagination: { current_page: 1, total: 0, page_size: 10, total_pages: 0 } };
  } catch (error) {
    console.error('Error fetching COSTECH funded projects:', error);
    if (error.response) {
      console.error('Response status:', error.response.status);
      console.error('Response data:', error.response.data);
    } else if (error.request) {
      console.error('No response received:', error.request);
    } else {
      console.error('Error setting up request:', error.message);
    }
    throw error;
  }
};

/**
 * Fetch funders list from API
 * @returns {Promise<Array>} - Array of funder items
 */
export const getFunders = async () => {
  try {

    const response = await axios.post(`${PROJECTS_API_BASE_URL}/funder/list`, {}, {
      timeout: 15000,
      headers: {
        'Content-Type': 'application/json',
      },
    });


    if (response.data && response.data.data && Array.isArray(response.data.data)) {
      const funders = response.data.data;
      return funders;
    }

    console.warn('API returned empty or invalid response');
    return [];
  } catch (error) {
    console.error('Error fetching funders:', error);
    throw error;
  }
};

/**
 * Fetch programs list from API
 * @returns {Promise<Array>} - Array of program items
 */
export const getPrograms = async () => {
  try {

    const response = await axios.post(`${PROJECTS_API_BASE_URL}/program/list`, {}, {
      timeout: 15000,
      headers: {
        'Content-Type': 'application/json',
      },
    });


    if (response.data && response.data.data && Array.isArray(response.data.data)) {
      const programs = response.data.data;
      return programs;
    }

    console.warn('API returned empty or invalid response');
    return [];
  } catch (error) {
    console.error('Error fetching programs:', error);
    throw error;
  }
};

/**
 * Fetch project statuses list from API
 * @returns {Promise<Array>} - Array of status items
 */
export const getProjectStatuses = async () => {
  try {

    const response = await axios.post(`${PROJECTS_API_BASE_URL}/project-status/list`, {}, {
      timeout: 15000,
      headers: {
        'Content-Type': 'application/json',
      },
    });


    if (response.data && response.data.data && Array.isArray(response.data.data)) {
      const statuses = response.data.data;
      return statuses;
    }

    console.warn('API returned empty or invalid response');
    return [];
  } catch (error) {
    console.error('Error fetching project statuses:', error);
    throw error;
  }
};

/**
 * Fetch genders list from API
 * @returns {Promise<Array>} - Array of gender items
 */
export const getGenders = async () => {
  try {

    const response = await axios.post(`${PROJECTS_API_BASE_URL}/gender/list`, {}, {
      timeout: 15000,
      headers: {
        'Content-Type': 'application/json',
      },
    });


    if (response.data && response.data.data && Array.isArray(response.data.data)) {
      const genders = response.data.data;
      return genders;
    }

    console.warn('API returned empty or invalid response');
    return [];
  } catch (error) {
    console.error('Error fetching genders:', error);
    throw error;
  }
};

/**
 * Fetch innovation statistics per gender
 * @returns {Promise<Object>} - Object with gender statistics
 */
export const getInnovationPerGender = async () => {
  try {

    const response = await axios.post(`${PROJECTS_API_BASE_URL}/statistics/innovation-per-gender`, {}, {
      timeout: 15000,
      headers: {
        'Content-Type': 'application/json',
      },
    });


    if (response.data && response.data.data) {
      return response.data.data;
    }

    console.warn('API returned empty or invalid response');
    return {};
  } catch (error) {
    console.error('Error fetching innovation per gender:', error);
    throw error;
  }
};

/**
 * Fetch innovation total funds per funder
 * @returns {Promise<Object>} - Object with funder statistics
 */
export const getInnovationTotalFundsPerFunder = async () => {
  try {

    const response = await axios.post(`${PROJECTS_API_BASE_URL}/statistics/innovation-total-funds-per-funder`, {}, {
      timeout: 15000,
      headers: {
        'Content-Type': 'application/json',
      },
    });


    if (response.data && response.data.data) {
      return response.data.data;
    }

    console.warn('API returned empty or invalid response');
    return {};
  } catch (error) {
    console.error('Error fetching innovation total funds per funder:', error);
    throw error;
  }
};

/**
 * Fetch innovation total funds per program
 * @returns {Promise<Object>} - Object with program statistics
 */
export const getInnovationTotalFundsPerProgram = async () => {
  try {

    const response = await axios.post(`${PROJECTS_API_BASE_URL}/statistics/innovation-total-funds-per-program`, {}, {
      timeout: 15000,
      headers: {
        'Content-Type': 'application/json',
      },
    });


    if (response.data && response.data.data) {
      return response.data.data;
    }

    console.warn('API returned empty or invalid response');
    return {};
  } catch (error) {
    console.error('Error fetching innovation total funds per program:', error);
    throw error;
  }
};

/**
 * Fetch innovation per status
 * @returns {Promise<Object>} - Object with status statistics
 */
export const getInnovationPerStatus = async () => {
  try {

    const response = await axios.post(`${PROJECTS_API_BASE_URL}/statistics/innovation-per-status`, {}, {
      timeout: 15000,
      headers: {
        'Content-Type': 'application/json',
      },
    });


    if (response.data && response.data.data) {
      return response.data.data;
    }

    console.warn('API returned empty or invalid response');
    return {};
  } catch (error) {
    console.error('Error fetching innovation per status:', error);
    throw error;
  }
};

/**
 * Fetch research statistics per gender
 * @returns {Promise<Object>} - Object with gender statistics
 */
export const getResearchPerGender = async () => {
  try {

    const response = await axios.post(`${PROJECTS_API_BASE_URL}/statistics/research-per-gender`, {}, {
      timeout: 15000,
      headers: {
        'Content-Type': 'application/json',
      },
    });


    if (response.data && response.data.data) {
      return response.data.data;
    }

    console.warn('API returned empty or invalid response');
    return {};
  } catch (error) {
    console.error('Error fetching research per gender:', error);
    throw error;
  }
};

/**
 * Fetch research total funds per funder
 * @returns {Promise<Object>} - Object with funder statistics
 */
export const getResearchTotalFundsPerFunder = async () => {
  try {

    const response = await axios.post(`${PROJECTS_API_BASE_URL}/statistics/research-total-funds-per-funder`, {}, {
      timeout: 15000,
      headers: {
        'Content-Type': 'application/json',
      },
    });


    if (response.data && response.data.data) {
      return response.data.data;
    }

    console.warn('API returned empty or invalid response');
    return {};
  } catch (error) {
    console.error('Error fetching research total funds per funder:', error);
    throw error;
  }
};

/**
 * Fetch research total funds per program
 * @returns {Promise<Object>} - Object with program statistics
 */
export const getResearchTotalFundsPerProgram = async () => {
  try {

    const response = await axios.post(`${PROJECTS_API_BASE_URL}/statistics/research-total-funds-per-program`, {}, {
      timeout: 15000,
      headers: {
        'Content-Type': 'application/json',
      },
    });


    if (response.data && response.data.data) {
      return response.data.data;
    }

    console.warn('API returned empty or invalid response');
    return {};
  } catch (error) {
    console.error('Error fetching research total funds per program:', error);
    throw error;
  }
};

/**
 * Fetch research per status
 * @returns {Promise<Object>} - Object with status statistics
 */
export const getResearchPerStatus = async () => {
  try {

    const response = await axios.post(`${PROJECTS_API_BASE_URL}/statistics/research-per-status`, {}, {
      timeout: 15000,
      headers: {
        'Content-Type': 'application/json',
      },
    });


    if (response.data && response.data.data) {
      return response.data.data;
    }

    console.warn('API returned empty or invalid response');
    return {};
  } catch (error) {
    console.error('Error fetching research per status:', error);
    throw error;
  }
};

/**
 * Fetch permit statistics per gender
 * @returns {Promise<Array>} - Array of gender statistics
 */
export const getPermitPerGender = async () => {
  try {

    const response = await axios.get(`${RIMS_API_BASE_URL}/permit-per-gender`, {
      timeout: 15000,
      headers: {
        'Content-Type': 'application/json',
      },
    });


    if (response.data && response.data.code === 200 && response.data.data) {
      return response.data.data;
    }

    console.warn('API returned empty or invalid response');
    return [];
  } catch (error) {
    console.error('Error fetching permit per gender:', error);
    throw error;
  }
};

/**
 * Fetch permit statistics per country
 * @returns {Promise<Array>} - Array of country statistics
 */
export const getPermitPerCountry = async () => {
  try {

    const response = await axios.get(`${RIMS_API_BASE_URL}/permit-per-country`, {
      timeout: 15000,
      headers: {
        'Content-Type': 'application/json',
      },
    });


    if (response.data && response.data.code === 200 && response.data.data) {
      return response.data.data;
    }

    console.warn('API returned empty or invalid response');
    return [];
  } catch (error) {
    console.error('Error fetching permit per country:', error);
    throw error;
  }
};

/**
 * Fetch permit statistics per sector
 * @returns {Promise<Array>} - Array of sector statistics
 */
export const getPermitPerSector = async () => {
  try {

    const response = await axios.get(`${RIMS_API_BASE_URL}/permit-per-sector`, {
      timeout: 15000,
      headers: {
        'Content-Type': 'application/json',
      },
    });


    if (response.data && response.data.code === 200 && response.data.data) {
      return response.data.data;
    }

    console.warn('API returned empty or invalid response');
    return [];
  } catch (error) {
    console.error('Error fetching permit per sector:', error);
    throw error;
  }
};

/**
 * Fetch permit statistics per research location (region)
 * @returns {Promise<Array>} - Array of region statistics
 */
export const getPermitPerRegion = async () => {
  try {

    const response = await axios.get(`${RIMS_API_BASE_URL}/permit-research-location`, {
      timeout: 15000,
      headers: {
        'Content-Type': 'application/json',
      },
    });


    if (response.data && response.data.code === 200 && response.data.data) {
      return response.data.data;
    }

    console.warn('API returned empty or invalid response');
    return [];
  } catch (error) {
    console.error('Error fetching permit per region:', error);
    throw error;
  }
};

/**
 * Fetch single COSTECH funded project detail by ID from API
 * @param {number|string} id - Project ID
 * @returns {Promise<Object>} - Project detail object
 */
export const getCostechFundedProjectDetail = async (id) => {
  try {

    const response = await axios.post(`${PROJECTS_API_BASE_URL}/project/show/${id}`, {}, {
      timeout: 15000,
      headers: {
        'Content-Type': 'application/json',
      },
    });


    if (response.data && response.data.id) {
      return response.data;
    }

    console.warn('API returned empty or invalid response');
    return null;
  } catch (error) {
    console.error('Error fetching COSTECH funded project detail:', error);
    if (error.response) {
      console.error('Response status:', error.response.status);
      console.error('Response data:', error.response.data);
    } else if (error.request) {
      console.error('No response received:', error.request);
    } else {
      console.error('Error setting up request:', error.message);
    }
    throw error;
  }
};

/**
 * Fetch HERIN statistics by region
 * @returns {Promise<Array>} - Array of region statistics
 */
export const getHerinStatsByRegion = async () => {
  try {
    const token = await getAuthToken();

    const response = await apiClient.get('/herin/istatsByRegion', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (response.data && response.data.status === 'OK' && response.data.returnData) {
      return response.data.returnData;
    }

    console.warn('API returned empty or invalid response');
    return [];
  } catch (error) {
    console.error('Error fetching HERIN stats by region:', error);
    if (error.response) {
      console.error('Response status:', error.response.status);
      console.error('Response data:', error.response.data);
    } else if (error.request) {
      console.error('No response received:', error.request);
    } else {
      console.error('Error setting up request:', error.message);
    }
    throw error;
  }
};

/**
 * Fetch HERIN statistics by category
 * @returns {Promise<Array>} - Array of category statistics
 */
export const getHerinStatsByCategory = async () => {
  try {
    const token = await getAuthToken();

    const response = await apiClient.get('/herin/istatsByCategory', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (response.data && response.data.status === 'OK' && response.data.returnData) {
      return response.data.returnData;
    }

    console.warn('API returned empty or invalid response');
    return [];
  } catch (error) {
    console.error('Error fetching HERIN stats by category:', error);
    if (error.response) {
      console.error('Response status:', error.response.status);
      console.error('Response data:', error.response.data);
    } else if (error.request) {
      console.error('No response received:', error.request);
    } else {
      console.error('Error setting up request:', error.message);
    }
    throw error;
  }
};

/**
 * Fetch public FAQs
 * @returns {Promise<Array>} - Array of FAQ categories with their FAQs
 */
export const getFAQs = async () => {
  try {
    const token = await getAuthToken();

    const response = await apiClient.get('/faq/ilistPublic', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (response.data && response.data.status === 'OK' && response.data.returnData && response.data.returnData.list_of_item) {
      return normalizeBackendItemList(response.data.returnData.list_of_item);
    }

    console.warn('API returned empty or invalid response for FAQs');
    return [];
  } catch (error) {
    console.error('Error fetching FAQs:', error);
    if (error.response) {
      console.error('Response status:', error.response.status);
      console.error('Response data:', error.response.data);
    } else if (error.request) {
      console.error('No response received:', error.request);
    } else {
      console.error('Error setting up request:', error.message);
    }
    throw error;
  }
};

/**
 * Fetch directorates list
 * @returns {Promise<Array>} - Array of directorates
 */
export const getDirectorates = async () => {
  try {
    const token = await getAuthToken();

    const response = await apiClient.get('/directorate/ilist', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (response.data && response.data.status === 'OK' && response.data.returnData && response.data.returnData.list_of_item) {
      return normalizeBackendItemList(response.data.returnData.list_of_item);
    }

    console.warn('API returned empty or invalid response for directorates');
    return [];
  } catch (error) {
    console.error('Error fetching directorates:', error);
    if (error.response) {
      console.error('Response status:', error.response.status);
      console.error('Response data:', error.response.data);
    } else if (error.request) {
      console.error('No response received:', error.request);
    } else {
      console.error('Error setting up request:', error.message);
    }
    throw error;
  }
};

/**
 * Fetch partners list
 * @returns {Promise<Array>} - Array of partners
 */
export const getPartners = async () => {
  try {
    const token = await getAuthToken();

    const response = await apiClient.get('/partners/ilist', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (response.data && response.data.status === 'OK' && response.data.returnData && response.data.returnData.list_of_item) {
      return normalizeBackendItemList(response.data.returnData.list_of_item);
    }

    console.warn('API returned empty or invalid response for partners');
    return [];
  } catch (error) {
    console.error('Error fetching partners:', error);
    if (error.response) {
      console.error('Response status:', error.response.status);
      console.error('Response data:', error.response.data);
    } else if (error.request) {
      console.error('No response received:', error.request);
    } else {
      console.error('Error setting up request:', error.message);
    }
    throw error;
  }
};

export default apiClient;
